import { styled } from "@linaria/react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { observer } from "mobx-react-lite"
import { useCallback, useRef } from "react"

import { TooltipModel } from "@/components/Tooltip/TooltipModel.ts"
import { useModel } from "@/hooks/useModel.ts"

import type { LinariaClassName } from "@linaria/core"
import type { PropsWithChildren, ReactNode } from "react"


type TooltipProps = PropsWithChildren & {
  open?: boolean
  onOpenChange?: (open: boolean) => void

  className?: LinariaClassName,
  align?: "center" | "start" | "end"
  side?: "top" | "right" | "bottom" | "left"

  content?: ReactNode

  ignoreEllipsis?: boolean
}

export const Tooltip = observer((props: TooltipProps) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const model = useModel(() => new TooltipModel(props.ignoreEllipsis), [props.ignoreEllipsis])

  const triggerRefCallback = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node
    model.setupObserver(node)
  }, [model])

  return (
    <TooltipPrimitive.Root delayDuration={350} open={props.open} onOpenChange={props.onOpenChange}>
      <TooltipPrimitive.Trigger
        asChild
        aria-label={typeof props.content === "string" ? props.content : undefined}
        ref={triggerRefCallback}
      >
        {props.children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        {model.hasTooltip.get() && props.content && (
          <TooltipContent
            className={props.className}
            align={props.align || "center"}
            side={props.side || "top"}
            sideOffset={8}
            sticky="always"
          >
            {props.content}
          </TooltipContent>
        )}
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
})

export const TooltipContent = styled(TooltipPrimitive.Content)`
  background: var(--tooltip-background-color);
  box-shadow: 0 2px 4px 0 oklch(0.0% 0 0deg / 15%);

  border-radius: var(--border-radius-6);
  padding: 5px 8px 6px;

  color: oklch(100% 0 0deg);
  font-size: var(--smaller-font-size);
  font-style: normal;
  font-weight: 600;
  line-height: 110%;

  z-index: var(--z-index-dropdown);

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: ${({ side }) => {
        switch (side) {
          case "top": return "translateY(5px)"
          case "bottom": return "translateY(-5px)"
          case "left": return "translateX(5px)"
          case "right": return "translateX(-5px)"
          default: return "translateY(0)"
        }
      }};
    }

    to {
      opacity: 1;
      transform: ${({ side }) => {
        switch (side) {
          case "top":
          case "bottom":
            return "translateY(0)"
          case "left":
          case "right":
            return "translateX(0)"
          default: return "translateY(0)"
        }
      }};
    }
  }

  &[data-state="delayed-open"] {
    animation: fade-in 0.15s ease-out;
  }

  &[data-state="closed"] {
    opacity: 0;
  }
`
